import React, { useEffect, useRef } from "react";

/* Calendly inline embed.
 *
 * • Loads https://assets.calendly.com/assets/external/widget.js exactly
 *   once across the whole app (idempotent singleton via window flag).
 * • Renders the canonical `<div class="calendly-inline-widget">` that
 *   the Calendly script auto-detects and hydrates.
 * • No tokens, no secrets. The widget URL (with color params) is provided
 *   via props and read from `CALENDLY_BOOKING_URL` env var server-side via
 *   /api/site-config — never hardcoded in the bundle.
 */

const WIDGET_SCRIPT_SRC =
  "https://assets.calendly.com/assets/external/widget.js";
const SCRIPT_FLAG = "__tmeCalendlyInlineLoaded";
const SCRIPT_ID = "tme-calendly-inline-script";

function ensureCalendlyScript() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window[SCRIPT_FLAG]) return;
  if (document.getElementById(SCRIPT_ID)) {
    window[SCRIPT_FLAG] = true;
    return;
  }
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.src = WIDGET_SCRIPT_SRC;
  s.async = true;
  document.body.appendChild(s);
  window[SCRIPT_FLAG] = true;
}

export default function CalendlyInline({
  url,
  refId = null,
  minHeight = 700,
  className = "",
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    ensureCalendlyScript();
  }, []);

  if (!url) {
    return (
      <div
        data-testid="calendly-inline-missing-url"
        className="rounded-2xl border border-clay/30 bg-sand-50 p-8 text-center"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
          Scheduler temporarily unavailable
        </p>
        <p className="mt-3 text-[14px] text-ink-muted max-w-md mx-auto">
          The booking link is being configured. Please use the intake form
          instead and we'll follow up to schedule.
        </p>
      </div>
    );
  }

  /* Merge a `utm_content=intake:<id>` correlator into the configured
   * Calendly URL when an intake ref is passed. Calendly forwards every
   * `utm_*` query param into `payload.tracking` on the webhook, letting
   * the backend match the booking back to the originating intake even
   * when the invitee email differs from the form email. */
  const widgetUrl = (() => {
    if (!refId) return url;
    const correlator = `intake:${refId}`;
    const [base, query = ""] = url.split("?");
    const params = new URLSearchParams(query);
    params.set("utm_source", "themodernexit_intake");
    params.set("utm_content", correlator);
    return `${base}?${params.toString()}`;
  })();

  return (
    <div
      ref={containerRef}
      data-testid="calendly-inline-widget"
      className={`calendly-inline-widget ${className}`.trim()}
      data-url={widgetUrl}
      data-intake-ref={refId || undefined}
      style={{ minWidth: 320, height: minHeight }}
    />
  );
}
